import { useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { fetchQuery, graphql } from "relay-runtime"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { DatePicker, VISUAL_DATE_FORMAT } from "@/components/ui/date-picker"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { DateOnly } from "@/lib/date-only"
import { getServerDateOnly } from "@/lib/initial-time-zone"
import {
  sbotSimulatorDialogSimulateAutomaticallyAssignDoctorQuery,
  sbotSimulatorDialogSimulateAutomaticallyAssignDoctorQuery$data,
} from "@/modules/admin/advanced/components/__generated__/sbotSimulatorDialogSimulateAutomaticallyAssignDoctorQuery.graphql"
import { RelayEnvironment } from "@/relay-environment"
import {
  Country,
  CountryValues,
  Language,
  LanguageValues,
  ServiceSpecialty,
  ServiceSpecialtyValues,
} from "@/types/__generated__/union-types"

const query = graphql`
  query sbotSimulatorDialogSimulateAutomaticallyAssignDoctorQuery($input: SimulateAutomaticallyAssignDoctorInput!) {
    simulateAutomaticallyAssignDoctor(input: $input) {
      assignedDoctor
      debugBagAsJson
    }
  }
`

const getDefaultValues: () => SbotSimulatorSchema = () => {
  return {
    country: "CH",
    date: getServerDateOnly(),
    slotId: 0,
    language: "GERMAN",
    specialty: "DERMATOLOGY",
    isMedicalSupervisor: false,
  }
}

const donwloadJsonFile = (json: string, filename: string) => {
  const blob = new Blob([json], {
    type: "application/json",
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

interface SbotSimulatorProps {
  onClose: () => void
}

const sbotSimulatorSchema = z.object({
  country: z.custom<Country>(),
  date: z.custom<DateOnly>(),
  slotId: z.number(),
  language: z.custom<Language>(),
  specialty: z.custom<ServiceSpecialty>(),
  isMedicalSupervisor: z.boolean(),
})

const handleFetch = (
  input: sbotSimulatorDialogSimulateAutomaticallyAssignDoctorQuery["variables"],
  next: (data: sbotSimulatorDialogSimulateAutomaticallyAssignDoctorQuery$data) => void,
) => {
  fetchQuery<sbotSimulatorDialogSimulateAutomaticallyAssignDoctorQuery>(RelayEnvironment, query, input, {
    fetchPolicy: "network-only",
  }).subscribe({ next })
}

type SbotSimulatorSchema = z.infer<typeof sbotSimulatorSchema>

const SbotSimulatorDialog = ({ onClose }: SbotSimulatorProps) => {
  const { t } = useTranslation()
  const [data, setData] = useState<sbotSimulatorDialogSimulateAutomaticallyAssignDoctorQuery$data | null>(null)
  const today = getServerDateOnly()

  const isDateInRange = (date: DateOnly) => date.isWithinIncludingRange(today, today.addMonths(3))

  const { control, resetField, handleSubmit, formState } = useForm<SbotSimulatorSchema>({
    resolver: zodResolver(sbotSimulatorSchema),
    defaultValues: getDefaultValues(),
  })

  const { isSubmitSuccessful } = formState

  let debugBagAsJson: string = "{}"
  if (data?.simulateAutomaticallyAssignDoctor?.debugBagAsJson) {
    const debugBag = JSON.parse(data.simulateAutomaticallyAssignDoctor.debugBagAsJson)
    debugBagAsJson = JSON.stringify(debugBag, null, 4)
    console.log("debugBag", debugBag)
    donwloadJsonFile(debugBagAsJson, "debug-sbot.json")
  }

  return (
    <Dialog
      data-testid="steering-level-dialog"
      open
      onOpenChange={() => {
        onClose()
      }}>
      <DialogContentCustom className="min-w-150.5" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Sbot Simulator</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2 px-6 py-2">
          <p>For a General consultation, choose:</p>
          <Controller
            control={control}
            name="country"
            render={({ formState: { defaultValues }, field: { onChange, value } }) => (
              <Select defaultValue={defaultValues?.language} onValueChange={onChange}>
                <SelectTrigger>
                  <SelectValue>
                    <LabelAndSelectValue label={"Country"} value={`${value}`} />
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <ScrollArea className="max-h-75">
                    <div className="p-2">
                      {CountryValues.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </div>
                  </ScrollArea>
                </SelectContent>
              </Select>
            )}
          />
          <Controller
            name="date"
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <DatePicker
                  disabledMatcher={(d) => {
                    return !isDateInRange(new DateOnly(d))
                  }}
                  readOnly={false}
                  data-testid="selected-date"
                  placeholder={VISUAL_DATE_FORMAT}
                  value={value.toDate()}
                  today={today.toDate()}
                  className="h-12"
                  onSelect={(v) => {
                    onChange(new DateOnly(v))
                  }}
                  onReset={() => {
                    resetField("date")
                  }}
                  wrapperClassName="w-57.5"
                  errorClasses="truncate"
                  translateError
                />
              )
            }}
          />
          <Controller
            control={control}
            name="slotId"
            render={({ formState: { defaultValues }, field: { onChange, value } }) => (
              <Select
                defaultValue={`${defaultValues?.slotId ?? 0}`}
                onValueChange={(v) => {
                  onChange(Number(v))
                }}>
                <SelectTrigger>
                  <SelectValue>
                    <LabelAndSelectValue label={"Slot selector"} value={`${value}`} />
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <ScrollArea className="max-h-75">
                    <div className="p-2">
                      {Array.from({ length: 48 }, (_, index) => (
                        <SelectItem key={index} value={`${index}`}>
                          {index}
                        </SelectItem>
                      ))}
                    </div>
                  </ScrollArea>
                </SelectContent>
              </Select>
            )}
          />

          <Controller
            control={control}
            name="language"
            render={({ formState: { defaultValues }, field: { onChange, value } }) => (
              <Select defaultValue={defaultValues?.language} onValueChange={onChange}>
                <SelectTrigger>
                  <SelectValue>
                    <LabelAndSelectValue label={"Language"} value={`${value}`} />
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <ScrollArea className="max-h-75">
                    <div className="p-2">
                      {LanguageValues.map((l) => (
                        <SelectItem key={l} value={l}>
                          {l}
                        </SelectItem>
                      ))}
                    </div>
                  </ScrollArea>
                </SelectContent>
              </Select>
            )}
          />
          <Controller
            control={control}
            name="specialty"
            render={({ formState: { defaultValues }, field: { onChange, value } }) => (
              <Select defaultValue={defaultValues?.language} onValueChange={onChange}>
                <SelectTrigger>
                  <SelectValue>
                    <LabelAndSelectValue label={"Specialty"} value={`${value}`} />
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <ScrollArea className="max-h-75">
                    <div className="p-2">
                      {ServiceSpecialtyValues.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </div>
                  </ScrollArea>
                </SelectContent>
              </Select>
            )}
          />
          <Controller
            control={control}
            name="isMedicalSupervisor"
            render={({ field: { onChange, value } }) => (
              <>
                <label>Medical Supervisor</label>
                <Switch checked={value} onCheckedChange={onChange} />
              </>
            )}
          />

          {isSubmitSuccessful ? (
            <div className="bg-surface-subdued flex max-h-80 flex-col text-xs">
              <div className="overflow-y-auto">
                <div className="mb-5">
                  Would be assigned to: <b>{data?.simulateAutomaticallyAssignDoctor.assignedDoctor ?? "Nobody"}</b>
                </div>
                <div>
                  Debug information:
                  <pre>
                    {JSON.stringify(
                      JSON.parse(data?.simulateAutomaticallyAssignDoctor?.debugBagAsJson ?? "{}"),
                      null,
                      4,
                    )}
                  </pre>
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <DialogFooter className="flex p-6 pt-4">
          <Button
            className="rounded-[20px] px-6 py-2.5"
            variant="secondary"
            onClick={onClose}
            data-testid="steering-level-dialog-cancel-button">
            {t("common.button.cancel")}
          </Button>
          <Button
            variant="primary"
            className="ml-0 rounded-[20px] px-6 py-2.5"
            onClick={() => {
              handleSubmit((v) => {
                handleFetch(
                  {
                    input: {
                      country: v.country,
                      date: v.date.toString(),
                      isMedicalSupervisor: v.isMedicalSupervisor,
                      language: v.language,
                      slotId: v.slotId,
                      specialty: v.specialty,
                    },
                  },
                  setData,
                )
              })()
            }}>
            Assign
          </Button>
        </DialogFooter>
      </DialogContentCustom>
    </Dialog>
  )
}

export { SbotSimulatorDialog }
